/**
 * useSystemStatus Hook
 *
 * Manages system status data fetching with mode switching and auto-refresh.
 */

import { useState, useCallback, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { format, subDays } from 'date-fns'
import {
  reportsApi,
  SystemStatusResponse,
  TimeMode
} from '../../../../services/reportsApi'

interface UseSystemStatusReturn {
  mode: TimeMode
  setMode: (mode: TimeMode) => void
  customDate: Date | null
  setCustomDate: (date: Date | null) => void
  data: SystemStatusResponse | undefined
  isLoading: boolean
  error: Error | null
  refetch: () => void
}

export const useSystemStatus = (): UseSystemStatusReturn => {
  const [mode, setMode] = useState<TimeMode>('today')
  const [customDate, setCustomDate] = useState<Date | null>(subDays(new Date(), 2))

  const getDateForMode = useCallback((): string | null => {
    const today = new Date()
    switch (mode) {
      case 'today':
        return format(today, 'yyyy-MM-dd')
      case 'yesterday':
        return format(subDays(today, 1), 'yyyy-MM-dd')
      case 'custom':
        return customDate ? format(customDate, 'yyyy-MM-dd') : null
      default: {
        const _exhaustiveCheck: never = mode
        return _exhaustiveCheck
      }
    }
  }, [mode, customDate])

  const dateForQuery = useMemo(() => getDateForMode(), [getDateForMode])

  const {
    data,
    isLoading,
    error,
    refetch
  } = useQuery<SystemStatusResponse, Error>({
    queryKey: ['system-status', mode, dateForQuery],
    queryFn: async () => {
      if (dateForQuery) {
        return reportsApi.getHistoricalStatus(dateForQuery)
      }
      throw new Error('Invalid date for query')
    },
    staleTime: 60000,
    enabled: !!dateForQuery,
  })

  const handleModeChange = useCallback((newMode: TimeMode) => {
    setMode(newMode)
  }, [])

  const handleDateChange = useCallback((date: Date | null) => {
    setCustomDate(date)
    if (date) {
      setMode('custom')
    }
  }, [])

  return {
    mode,
    setMode: handleModeChange,
    customDate,
    setCustomDate: handleDateChange,
    data,
    isLoading,
    error: error || null,
    refetch,
  }
}

export default useSystemStatus
